import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { EVENT_2026_PHOTOS } from "@/lib/platform";

export default function Gallery2026() {
  const [lightbox, setLightbox] = useState(null);
  const navigate = useNavigate();

  const next = () => setLightbox((l) => (l + 1) % EVENT_2026_PHOTOS.length);
  const prev = () => setLightbox((l) => (l - 1 + EVENT_2026_PHOTOS.length) % EVENT_2026_PHOTOS.length);

  return (
    <section id="gallery" data-testid="gallery-2026" className="relative bg-[#07152B] text-[#F5EBD9] py-24 lg:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="mb-12 lg:mb-16"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5892E] mb-4 flex items-center gap-4">
            <span className="h-px w-10 bg-[#F5892E]" />
            Варна · 24 октомври 2026
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight max-w-5xl">
            Варна 2026. <span className="text-[#F5892E]">Това беше само началото.</span>
          </h2>
          <p className="mt-6 text-lg text-[#C8D4E6] leading-relaxed max-w-[62ch]">
            Една сцена. Една общност. Един разговор за бъдещето на имотния пазар. На 24 октомври
            2026 г. събрахме хора с общ интерес към недвижимите имоти, инвестициите и финансовите
            решения. Сега продължаваме напред.
          </p>
        </motion.div>
      </div>

      {/* Immersive photo grid */}
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {EVENT_2026_PHOTOS.map((p, i) => (
            <motion.button
              key={p.src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              onClick={() => setLightbox(i)}
              data-testid={`gallery-photo-${i}`}
              className={`group relative overflow-hidden rounded-xl cursor-pointer ${
                i === 0 ? "col-span-2 lg:col-span-2 aspect-[16/9]" : "aspect-[4/3]"
              }`}
            >
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-[#0B1F3D]/25 group-hover:bg-[#0B1F3D]/0 transition-colors duration-500" />
            </motion.button>
          ))}
        </div>

        {/* Recap video placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          data-testid="recap-video-placeholder"
          className="mt-4 relative overflow-hidden rounded-xl border border-[#1E3A63] bg-[#0F284B] aspect-video flex flex-col items-center justify-center text-center px-6"
        >
          <div className="w-16 h-16 rounded-full border-2 border-[#F5892E] flex items-center justify-center mb-4">
            <div className="w-0 h-0 border-t-8 border-b-8 border-l-12 border-transparent border-l-[#F5892E] ml-1" />
          </div>
          <div className="font-display uppercase text-xl sm:text-2xl">Видео от събитието</div>
          <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[#8A9EB8]">
            Очаквайте скоро — официалното recap видео от Варна 2026
          </div>
        </motion.div>
      </div>

      {/* Past → future transition */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9 }}
          className="mt-20 lg:mt-28 text-center"
        >
          <div className="font-display uppercase text-3xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight">
            2026 беше началото.<br />
            <span className="text-[#F5892E]">2027 е следващото ниво.</span>
          </div>
          <button
            onClick={() => { navigate("/varna-2027"); window.scrollTo({ top: 0 }); }}
            data-testid="gallery-cta-varna"
            className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-sm uppercase tracking-widest hover:bg-[#F5EBD9] transition-colors"
          >
            Бъди част от Варна 2027
            <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
          </button>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] bg-[#050d1e]/95 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              onClick={() => setLightbox(null)}
              aria-label="Затвори"
              data-testid="lightbox-close"
              className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center border border-[#F5EBD9]/30 rounded-full text-[#F5EBD9] hover:border-[#F5892E] hover:text-[#F5892E] transition-colors z-10"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Предишна"
              data-testid="lightbox-prev"
              className="absolute left-3 sm:left-6 w-11 h-11 flex items-center justify-center border border-[#F5EBD9]/30 rounded-full text-[#F5EBD9] hover:border-[#F5892E] hover:text-[#F5892E] transition-colors z-10"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2} />
            </button>
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              src={EVENT_2026_PHOTOS[lightbox].src}
              alt={EVENT_2026_PHOTOS[lightbox].alt}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Следваща"
              data-testid="lightbox-next"
              className="absolute right-3 sm:right-6 w-11 h-11 flex items-center justify-center border border-[#F5EBD9]/30 rounded-full text-[#F5EBD9] hover:border-[#F5892E] hover:text-[#F5892E] transition-colors z-10"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={2} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
